import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef, inject, output, EventEmitter, Output } from "@angular/core";
import { Subscription } from "rxjs";
import { AuthService } from "../auth";
import { HelpersService } from "../helpers-service/helpers.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { Router } from "@angular/router";
import { applicationStringFromApplicationType, ApplicationType, isJobsBilling } from "src/app/views-interfaces/ApplicationType.enum";
import type { DashboardSelectCanChangeArgs } from "./dashboard-select.interface";
import { ApplicationsService } from "../applications-service/applications.service";
import { TranslateService } from "@ngx-translate/core";
import { IDashboardModel } from "@app/views-interfaces";

@Component({
    selector: "wk-dashboard-select",
    templateUrl: "./dashboard-select.component.html",
    styleUrl: "./dashboard-select.component.scss",
    standalone: false,
    host: { class: "wk-dashboard-select" },
})
export class DashboardSelectComponent implements OnInit, OnDestroy {
    authService = inject(AuthService);
    private dashboardsService = inject(DashboardsService);
    private helpersService = inject(HelpersService);
    private applicationsService = inject(ApplicationsService);
    private router = inject(Router);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private translate = inject(TranslateService);

    @Input() showAll = false; // Show all dashboards regardless of users access
    @Input() showDashboardRenameOption = false; // Show rename dashboard button
    @Input() checkDashboardVisibility: (IDashboardModel) => boolean | null = null; // Function to determine if a dashboard is visible in the list
    @Output() canChangeDashboard = new EventEmitter<DashboardSelectCanChangeArgs>();
    readonly dashboardChangeCompleted = output<boolean>();

    titleChangedSubscription: Subscription;
    availableDashboards = [];
    loadingDashboard = true;
    currentDashboardId: string;
    currentDashboard: any;
    dashboardItems = [];
    dashboardTitle = "";
    isDropdownOpen = false;
    selectedYear: string;
    selectedApp: string;
    selectedDashboard: string;
    private order = ["Taxprep", "iFirm Taxprep"];
    lang: string;

    constructor() {
        const dashboardsService = this.dashboardsService;

        this.titleChangedSubscription = dashboardsService.titleChangedEvent$.subscribe((title) => {
            this.currentDashboard.Title = title;
            this.dashboardItems.forEach((app) => {
                const dashboards = app.years ? app.years.flatMap((year) => year.dashboards) : (app.dashboards ?? []);
                const dashboard = dashboards.find((d) => d.id === this.currentDashboard.Id);
                if (dashboard) {
                    dashboard.name = title;
                }
            });
            this.handleDashboardNameClick({ name: title, id: this.currentDashboard.Id }, false);
            this.selectedDashboard = title;
            this.changeDetectorRef.detectChanges();
        });
        this.dashboardsService.dashboardsLoadedEvent$.subscribe(async (data) => {
            await this.init();
        });
    }

    async ngOnInit() {
        await this.init();
    }

    fillDashboardItems() {
        this.dashboardItems = [];
        this.availableDashboards.forEach((dashboard) => {
            if (!this.checkDashboardVisibility || this.checkDashboardVisibility(dashboard)) {
                const applicationTypeLabel = isJobsBilling(dashboard.ApplicationType)
                    ? this.translate.instant("Dashboard.jobsAndBilling.Title")
                    : applicationStringFromApplicationType(dashboard.ApplicationType);

                let app = this.dashboardItems.find((it) => it.label === applicationTypeLabel);
                if (!app) {
                    if (isJobsBilling(dashboard.ApplicationType)) {
                        app = { label: this.translate.instant("Dashboard.jobsAndBilling.Title"), flavor: "J&B", dashboards: [] };
                        app.dashboards.push({ name: dashboard.Title, id: dashboard.Id, applicationType: dashboard.ApplicationType });
                    } else {
                        app = { label: applicationStringFromApplicationType(dashboard.ApplicationType), years: [] };
                    }

                    this.dashboardItems.push(app);
                } else if (isJobsBilling(dashboard.ApplicationType)) {
                    app.dashboards.push({ name: dashboard.Title, id: dashboard.Id, applicationType: dashboard.ApplicationType });
                }

                if (isJobsBilling(dashboard.ApplicationType)) {
                    if (!this.order.includes(app.label)) {
                        this.order.push(app.label);
                    }
                }

                if (!isJobsBilling(dashboard.ApplicationType)) {
                    let year = app.years.find((it) => it.label === dashboard.TaxYear);
                    if (!year) {
                        year = { label: dashboard.TaxYear, dashboards: [] };
                        app.years.push(year);
                    }
                    year.dashboards.push({ name: dashboard.Title, id: dashboard.Id });
                }

                if (this.currentDashboardId === dashboard.Id) {
                    this.handleDashboardNameClick(
                        dashboard.ApplicationType === ApplicationType.JobsBilling
                            ? { name: dashboard.Title, id: dashboard.Id, applicationType: dashboard.ApplicationType }
                            : { name: dashboard.Title, id: dashboard.Id },
                        false,
                    );
                }
            }
        });
        this.dashboardItems.forEach((app) => {
            if (app.years) {
                app.years.forEach((year) => {
                    year.dashboards = year.dashboards.sort((a, b) => a.name.localeCompare(b.name));
                });
                app.years = app.years.sort((a, b) => parseInt(b.label) - parseInt(a.label));
            }
        });

        this.dashboardItems.forEach((app) => {
            if (app.flavor === "J&B" && Array.isArray(app.dashboards)) {
                app.dashboards = app.dashboards.sort((a, b) => a.name.localeCompare(b.name));
            }
        });

        this.dashboardItems = this.dashboardItems.sort((a, b) => this.order.indexOf(a.label) - this.order.indexOf(b.label));
    }

    async init() {
        this.lang = this.helpersService.getLang();
        this.currentDashboardId = (await this.dashboardsService.getCurrentDashboard(this.showAll)).Id;
        if (!this.showAll) {
            const userUds = this.helpersService.userDashboardSettingsService.getUserDashboardSettingsByUserId(this.authService.currentUser.sub);
            this.availableDashboards = await this.applicationsService.getAccessibleDashboards();
            this.availableDashboards = this.availableDashboards.filter((dashboard) => {
                const userDashboardUds = userUds.find((it) => it.DashboardId === dashboard.Id);
                return !userDashboardUds || userDashboardUds.Active;
            });
        } else {
            this.availableDashboards = await this.dashboardsService.getDashboardsList();
            if (this.availableDashboards.length > 1) {
                this.availableDashboards.sort((a, b) => {
                    if (a.Title.toLowerCase() < b.Title.toLowerCase()) {
                        return -1;
                    } else if (a.Title.toLowerCase() > b.Title.toLowerCase()) {
                        return 1;
                    } else {
                        return 0;
                    }
                });
            }
        }
        this.currentDashboard = this.availableDashboards.find((dashboard) => dashboard.Id == this.currentDashboardId);

        this.fillDashboardItems();

        if (this.availableDashboards.length === 0) {
            this.router.navigate(["/accessdenied"]);
        } else if (!this.availableDashboards.some((dashboard) => this.dashboardsService.getCurrentDashboardId() === dashboard.Id)) {
            this.helpersService.changeDashboard(this.availableDashboards[0].Id);
        }
        this.loadingDashboard = false;
    }

    ngOnDestroy() {
        this.titleChangedSubscription.unsubscribe();
    }

    changeDashboard(dashboardId) {
        if (this.canChangeDashboard.observed) {
            const dashboardSelectCanChangeArgs: DashboardSelectCanChangeArgs = {
                dashboardId,
                action: this.doChangeDashboard.bind(this),
                cancelAction: this.doCancel.bind(this),
            };
            this.canChangeDashboard.emit(dashboardSelectCanChangeArgs);
        } else {
            this.doChangeDashboard(dashboardId);
        }
    }

    async doChangeDashboard(dashboardId) {
        if (this.showAll) {
            this.currentDashboardId = dashboardId;
            this.currentDashboard = this.availableDashboards.find((dashboard) => dashboard.Id === dashboardId);
            this.dashboardChangeCompleted.emit(true);
        } else {
            this.loadingDashboard = true;
            await this.helpersService.changeDashboard(dashboardId);
            this.currentDashboardId = dashboardId;
            this.currentDashboard = this.availableDashboards.find((dashboard) => dashboard.Id === dashboardId);
            this.dashboardChangeCompleted.emit(true);
            this.loadingDashboard = false;
        }
    }

    doCancel() {
        // force refresh
        const availableDashboards = this.availableDashboards;
        this.availableDashboards = [];
        this.changeDetectorRef.detectChanges();
        this.availableDashboards = availableDashboards;
        this.changeDetectorRef.detectChanges();
        this.handleDashboardNameClick({ name: this.currentDashboard.Title, id: this.currentDashboard.Id }, false);
    }

    handleDashboardNameClick(newDashboard, isDashboardChanged = true) {
        if (!isDashboardChanged || this.currentDashboardId != newDashboard.id) {
            let app: any = [];

            if (isJobsBilling(newDashboard?.applicationType)) {
                app = this.dashboardItems.find((item) => Array.isArray(item.dashboards) && item.dashboards.some((d) => d.id === newDashboard.id));
            } else {
                app = this.dashboardItems.find(
                    (it) =>
                        Array.isArray(it.years) &&
                        it.years.some(
                            (year: any) =>
                                Array.isArray(year.dashboards) && year.dashboards.some((dashboard: any) => dashboard.id === newDashboard?.id),
                        ),
                );
            }

            if (app) {
                this.selectedApp = app.label;

                if (newDashboard?.applicationType === ApplicationType.JobsBilling) {
                    this.selectedDashboard = newDashboard.name;
                    this.dashboardTitle = newDashboard.name;
                } else {
                    const year = app.years.find((year) => year.dashboards.some((dashboard) => dashboard.id === newDashboard.id));
                    if (year) {
                        this.selectedYear = year.label;
                        this.selectedDashboard = newDashboard.name;
                        this.dashboardTitle = `${year.label} - ${newDashboard.name}`;
                    }
                }
            }
            if (isDashboardChanged) {
                this.changeDashboard(newDashboard?.id);
            }
        }
    }

    setDropdownState(event: boolean) {
        this.isDropdownOpen = event;
    }
}
